import { AfterContentChecked } from '@angular/core';
import { Component } from '@angular/core';
import { ContentChildren } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { ElementRef } from '@angular/core';
import { forwardRef } from '@angular/core';
import { HostBinding } from '@angular/core';
import { HostListener } from '@angular/core';
import { Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { OnDestroy } from '@angular/core';
import { QueryList } from '@angular/core';
import { TemplateRef } from '@angular/core';
import { ViewChild } from '@angular/core';
import { ViewContainerRef } from '@angular/core';

import { noop, Subscription, merge } from 'rxjs';


import { UIOptionComponent } from './option.component';
import { Overlay } from 'app/ui/core/overlay';
import { OverlayRef } from 'app/ui/core/overlay-ref';
import { OVERLAY_PROVIDERS } from 'app/ui/core/overlay';
import { TemplatePortal } from 'app/ui/core/portal';

const SELECTBOX_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => UISelectboxComponent),
    multi: true
};

@Component({
    selector: 'ui-selectbox',
    templateUrl: 'selectbox.component.html',
    styleUrls: ['selectbox.component.scss'],
    providers: [OVERLAY_PROVIDERS, SELECTBOX_CONTROL_VALUE_ACCESSOR]
})
export class UISelectboxComponent implements AfterContentChecked, ControlValueAccessor, OnDestroy {

    private _backdropSubscription: Subscription;

    private _portal: TemplatePortal;

    @HostBinding('class.active')
    private _overlayRef: OverlayRef;

    private _optionSubscription: Subscription;

    @ViewChild(TemplateRef)
    templateRef: TemplateRef<any>;

    @Input()
    placeholder: string;

    @ContentChildren(UIOptionComponent)
    options: QueryList<UIOptionComponent>;

    value: any;

    private _onChange: Function = noop;

    private _onTouched: Function = noop;

    constructor(private _elementRef: ElementRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef) {
    }

    ngAfterContentChecked(): void {
        if (this._optionSubscription) {
            this._optionSubscription.unsubscribe();
        }
        const emitters = merge(...this.options.map(option => option.onSelectOption));
        this._optionSubscription = emitters.subscribe((option: UIOptionComponent) => this._onSelect(option));
    }

    ngOnDestroy(): void {
        this._destroyOptions();
        this._destroyOverlay();
    }

    @HostListener('click')
    private _onClick(): void {
        this._createOverlay();
        this._overlayRef.attach(this._portal);
        this._subscribeToBackdrop();
        this._updatePosition();
    }

    @HostListener('window:scroll')
    private _onScroll(): void {
      this._updatePosition();
    }

    @HostListener('window:resize')
    private _onResize(): void {
      this._updatePosition();
    }

    private _updatePosition(): void {
      if (!this._elementRef || !this._overlayRef) return;

      const elementBox = this._elementRef.nativeElement.getBoundingClientRect();
      this._overlayRef.overlayElement.style.left = elementBox.left + window.scrollX + 'px';
      this._overlayRef.overlayElement.style.top = (elementBox.bottom - 1) + window.scrollY +  'px';
      this._overlayRef.overlayElement.style.minWidth = (elementBox.right - elementBox.left) + 'px';
    }

    private _onSelect(selectedOption: UIOptionComponent): void {
        this.options.forEach(option => (option.selected = false));
        selectedOption.selected = true;
        this.value = selectedOption.viewValue;
        this._onChange(selectedOption.value);
        this._onTouched();
        this._destroyOverlay();
    }

    private _setOptionByValue(value: any) {
        this.options.forEach(option => {
            if (option.value === value) {
                this._onSelect(option);
            }
        });
    }

    private _createOverlay(): void {
        if (!this._overlayRef) {
            this._portal = new TemplatePortal(this.templateRef, this._viewContainerRef);
            this._overlayRef = this._overlay.create({ hasBackdrop: true });
        }
    }

    private _destroyOverlay(): void {
        if (this._overlayRef) {
            this._overlayRef.dispose();
            this._overlayRef = null;
        }
    }

    private _subscribeToBackdrop(): void {
        this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
            this._destroyOverlay();
        });
    }

    private _destroyOptions() {
        if (this._optionSubscription) {
            this._optionSubscription.unsubscribe();
            this._optionSubscription = null;
        }
    }

    writeValue(value: any): void {
        if (this.options) {
            this._setOptionByValue(value);
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }
}
